import {
  Text,
  Heading,
  Icon,
  InlineStack,
  Stack,
  Panel,
  PanelSection,
  PopoverTrigger,
  PopoverContent,
  Popover,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { ServicesApiEnvHealthCountStat } from '@/services/api/services-api';
import { calc } from '@vanilla-extract/css-utils';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
interface StatCardProps {
  icon: IconName;
  label: string;
  description: string;
  value: string;
  unit: string;
  prevDayValue?: number;
  currentValue?: number;
  processedToday?: Record<string, ServicesApiEnvHealthCountStat>;
}

export function formatDeltaPct(current: number, previous: number): string | null {
  if (previous <= 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 1) return '0%';
  const absPct = Math.abs(pct);
  const formatted = absPct >= 10 ? Math.round(absPct).toLocaleString() : absPct.toFixed(1);
  return `${pct > 0 ? '+' : '-'}${formatted}%`;
}

export function formatDelta(
  current: number,
  previous: number,
): { text: string; icon: IconName; color: 'critical' | 'success' | 'subtle' } | null {
  const pct = formatDeltaPct(current, previous);
  if (pct == null) return null;
  const direction = current >= previous ? 1 : -1;
  if (pct === '0%') return { text: '0%', icon: 'no-change', color: 'subtle' };
  return {
    text: pct.slice(1),
    icon: direction > 0 ? 'arrow-up' : 'arrow-down',
    color: direction > 0 ? 'success' : 'critical',
  };
}

const PROCESSED_TODAY_EXCLUDED_KEYS = new Set(['egress', 'outgoing', 'total']);

const PROCESSED_TODAY_BREAKDOWN_CONFIG: Record<string, { label: string; icon: IconName }> = {
  identify: { label: 'Identify', icon: 'person' },
  events: { label: 'Events', icon: 'trigger-event' },
};

const PROCESSED_TODAY_BREAKDOWN_ORDER = ['identify', 'events'];

function getProcessedTodayBreakdownEntries(processedToday: Record<string, ServicesApiEnvHealthCountStat> | undefined) {
  if (processedToday == null) return [];
  const entries = Object.entries(processedToday)
    .filter(([key]) => !PROCESSED_TODAY_EXCLUDED_KEYS.has(key))
    .map(([key, stat]) => {
      const config = PROCESSED_TODAY_BREAKDOWN_CONFIG[key];
      return {
        key,
        label: config?.label ?? key,
        icon: config?.icon ?? 'database',
        current: stat.current ?? 0,
        prevDay: stat.prev_day ?? 0,
      };
    });
  const orderIndex = (key: string) => {
    const index = PROCESSED_TODAY_BREAKDOWN_ORDER.indexOf(key);
    return index === -1 ? PROCESSED_TODAY_BREAKDOWN_ORDER.length : index;
  };
  return entries.sort((a, b) => orderIndex(a.key) - orderIndex(b.key));
}

export function StatCard({
  icon,
  label,
  description,
  value,
  unit,
  prevDayValue,
  currentValue,
  processedToday,
}: StatCardProps) {
  const delta = currentValue != null && prevDayValue != null ? formatDelta(currentValue, prevDayValue) : null;

  return (
    <Panel justifyContent="space-between">
      <PanelSection pb="100">
        <InlineStack alignBlock="center" alignInline="space-between">
          <Icon name={icon} />
          {delta != null ? (
            <InlineStack gap="100" alignBlock="center">
              <Popover placement="top-start" shouldTriggerOnClick={false} shouldTriggerOnHover={true}>
                <PopoverTrigger>
                  <InlineStack gap="025" alignBlock="center" cursor="help">
                    <Icon name={delta.icon} size="xs" color={delta.color} />
                    <Text weight="semibold" size="xs" color={delta.color}>
                      {delta.text}
                    </Text>
                  </InlineStack>
                </PopoverTrigger>

                <PopoverContent style={{ width: calc(themeVars.space['1600']).multiply(2.2).toString() }}>
                  <Stack gap="100">
                    {processedToday != null
                      ? getProcessedTodayBreakdownEntries(processedToday).map(
                          ({ key, label, icon, current, prevDay }) => {
                            const formattedDelta = formatDelta(current, prevDay);
                            return (
                              <InlineStack key={key} alignBlock="center" alignInline="space-between">
                                <InlineStack gap="050" alignBlock="center" alignInline="start">
                                  <InlineStack gap="050" alignBlock="center" alignInline="space-between">
                                    <Icon name={icon} />
                                    <Text weight="semibold">{label}</Text>
                                  </InlineStack>
                                  {formattedDelta != null ? (
                                    <InlineStack gap="025" alignBlock="center" alignInline="space-between">
                                      <Icon size="xs" color={formattedDelta.color} name={formattedDelta.icon} />
                                      <Text weight="semibold" color={formattedDelta.color}>
                                        {formattedDelta.text}
                                      </Text>
                                    </InlineStack>
                                  ) : null}
                                </InlineStack>
                                <Text weight="semibold">{current.toLocaleString()}</Text>
                              </InlineStack>
                            );
                          },
                        )
                      : null}
                    <Text size="xxs" color="subtle">
                      compared to yesterday at this time
                    </Text>
                  </Stack>
                </PopoverContent>
              </Popover>
            </InlineStack>
          ) : null}
        </InlineStack>
      </PanelSection>
      <PanelSection h="full" pb="100">
        <Stack gap="100">
          <Heading level="3">{label}</Heading>
          <Text size="s" color="subtle">
            {description}
          </Text>
        </Stack>
      </PanelSection>
      <PanelSection>
        <InlineStack gap="100" alignBlock="center">
          <InlineStack gap="050" alignBlock="baseline">
            <Text size="l" weight="semibold">
              {value}
            </Text>
            <Text size="s" color="subtle">
              {unit}
            </Text>
          </InlineStack>
        </InlineStack>
      </PanelSection>
    </Panel>
  );
}
